import type { ListingSurfaceConfig, PlanDefinition, PlatformFeatureFlags } from '@shopgnd/shared';
import { DEFAULT_PLAN_DEFINITIONS, mergePlanDefinitions } from '@shopgnd/shared';
import {
  getPlatformSettings,
  updatePlatformSettings,
  type PlatformSettings,
} from '@/api/platformSettings';
import {
  applyPlanDefinitions,
  parsePlanDefinitionsJson,
  resolvePlanDefinitionsFromSettings,
  serializePlanDefinitions,
} from '@/lib/platformPlanConfig';
import {
  applyListingSurfaces,
  getRuntimeSurface,
  parseListingSurfacesJson,
  serializeListingSurfaces,
} from '@/lib/platformSurfaceConfig';
import {
  applyHomepageHero,
  parseHomepageHeroJson,
  serializeHomepageHero,
} from '@/lib/platformHeroConfig';
import {
  applyFeatureFlags,
  parseFeatureFlagsJson,
  serializeFeatureFlags,
} from '@/lib/platformFeatureFlagsRuntime';
import type { HomepageHeroConfig } from '@shopgnd/shared';
import {
  applySiteSocialLinks,
  parseSiteSocialLinksJson,
  serializeSiteSocialLinks,
  type SiteSocialLinksPayload,
} from '@/lib/platformSiteSocial';

function syncAllFromSettings(settings: PlatformSettings): void {
  applyPlanDefinitions(resolvePlanDefinitionsFromSettings(settings));
  applyListingSurfaces(parseListingSurfacesJson(settings.listingSurfacesJson));
  applyFeatureFlags(parseFeatureFlagsJson(settings.featureFlagsJson));
  applyHomepageHero(parseHomepageHeroJson(settings.homepageHeroJson));
  applySiteSocialLinks(parseSiteSocialLinksJson(settings.siteSocialLinksJson));
}

export async function getPlanDefinitions(): Promise<PlanDefinition[]> {
  const settings = await getPlatformSettings();
  const plans = resolvePlanDefinitionsFromSettings(settings);
  applyPlanDefinitions(plans);
  return plans;
}

export async function getListingSurfaces(): Promise<ListingSurfaceConfig[]> {
  const settings = await getPlatformSettings();
  const surfaces = parseListingSurfacesJson(settings.listingSurfacesJson);
  applyListingSurfaces(surfaces);
  return surfaces;
}

export async function getFeatureFlags(): Promise<PlatformFeatureFlags> {
  const settings = await getPlatformSettings();
  const flags = parseFeatureFlagsJson(settings.featureFlagsJson);
  applyFeatureFlags(flags);
  return flags;
}

export async function getHomepageHero(): Promise<HomepageHeroConfig> {
  const settings = await getPlatformSettings();
  const hero = parseHomepageHeroJson(settings.homepageHeroJson);
  applyHomepageHero(hero);
  return hero;
}

export async function updateHomepageHero(config: HomepageHeroConfig): Promise<HomepageHeroConfig> {
  await updatePlatformSettings({ homepageHeroJson: serializeHomepageHero(config) });
  applyHomepageHero(config);
  return config;
}

export async function fetchAdminPlatformSettingsForm(): Promise<{
  plans: PlanDefinition[];
  surfaces: ListingSurfaceConfig[];
  featureFlags: PlatformFeatureFlags;
  hero: HomepageHeroConfig;
  settings: PlatformSettings;
}> {
  const snapshot = await getPlatformConfigSnapshot();
  return {
    ...snapshot,
    hero: parseHomepageHeroJson(snapshot.settings.homepageHeroJson),
  };
}

export async function getPlatformConfigSnapshot(): Promise<{
  plans: PlanDefinition[];
  surfaces: ListingSurfaceConfig[];
  featureFlags: PlatformFeatureFlags;
  settings: PlatformSettings;
}> {
  const settings = await getPlatformSettings();
  syncAllFromSettings(settings);
  return {
    plans: resolvePlanDefinitionsFromSettings(settings),
    surfaces: parseListingSurfacesJson(settings.listingSurfacesJson),
    featureFlags: parseFeatureFlagsJson(settings.featureFlagsJson),
    settings,
  };
}

export function commissionBpsPatchFromPlans(plans: PlanDefinition[]): {
  starterCommissionBps: number;
  growthCommissionBps: number;
  proCommissionBps: number;
} {
  const starter = plans.find((p) => p.id === 'starter');
  const growth = plans.find((p) => p.id === 'growth');
  const pro = plans.find((p) => p.id === 'pro');
  return {
    starterCommissionBps: starter?.commissionBps ?? DEFAULT_PLAN_DEFINITIONS[0].commissionBps,
    growthCommissionBps: growth?.commissionBps ?? DEFAULT_PLAN_DEFINITIONS[1].commissionBps,
    proCommissionBps: pro?.commissionBps ?? DEFAULT_PLAN_DEFINITIONS[2].commissionBps,
  };
}

export async function updatePlanDefinitions(plans: PlanDefinition[]): Promise<PlanDefinition[]> {
  const merged = mergePlanDefinitions(plans.length ? plans : undefined);
  await updatePlatformSettings({
    planDefinitionsJson: serializePlanDefinitions(merged),
    ...commissionBpsPatchFromPlans(merged),
  });
  applyPlanDefinitions(merged);
  return merged;
}

export async function updateListingSurfaces(surfaces: ListingSurfaceConfig[]): Promise<ListingSurfaceConfig[]> {
  await updatePlatformSettings({ listingSurfacesJson: serializeListingSurfaces(surfaces) });
  applyListingSurfaces(surfaces);
  return surfaces;
}

export async function updateFeatureFlags(flags: PlatformFeatureFlags): Promise<PlatformFeatureFlags> {
  await updatePlatformSettings({ featureFlagsJson: serializeFeatureFlags(flags) });
  applyFeatureFlags(flags);
  return flags;
}

export async function getSiteSocialLinksConfig(): Promise<SiteSocialLinksPayload> {
  const settings = await getPlatformSettings();
  const payload = parseSiteSocialLinksJson(settings.siteSocialLinksJson);
  applySiteSocialLinks(payload);
  return payload;
}

export async function updateSiteSocialLinksConfig(
  payload: SiteSocialLinksPayload,
): Promise<SiteSocialLinksPayload> {
  await updatePlatformSettings({ siteSocialLinksJson: serializeSiteSocialLinks(payload) });
  applySiteSocialLinks(payload);
  return payload;
}

export async function loadPlatformConfig(): Promise<void> {
  const settings = await getPlatformSettings();
  syncAllFromSettings(settings);
}
